import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp, DollarSign, CalendarCheck, XCircle } from 'lucide-react';
import { CURRENCY_SYMBOL } from '../../config/brand';

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/reports').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;
  if (!data) return <p className="text-[#5A5A5A]">Failed to load reports</p>;

  const summaryCards = [
    { label: 'Total Bookings', value: data.total_bookings, icon: CalendarCheck, color: '#4A90E2' },
    { label: 'Completed', value: data.completed, icon: TrendingUp, color: '#4A7C59' },
    { label: 'Cancelled', value: data.cancelled, icon: XCircle, color: '#D9534F' },
    { label: 'No-Shows', value: data.no_shows, icon: XCircle, color: '#E8B931' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-reports-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Reports</h1>
        <p className="text-sm text-[#5A5A5A] mt-1">Business performance overview</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summaryCards.map((c, i) => (
          <Card key={i} className="rounded-2xl border-[#E5E5E0] bg-white" data-testid={`report-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${c.color}15` }}>
                <c.icon className="w-5 h-5" style={{ color: c.color }} strokeWidth={1.5} />
              </div>
              <p className="text-2xl font-bold text-[#2D2D2D]">{c.value}</p>
              <p className="text-xs text-[#888] mt-1">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="rounded-2xl border-[#E5E5E0] bg-white" style={{ boxShadow: 'var(--shadow-soft)' }}>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4">Monthly Revenue</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#5A5A5A' }} />
                <YAxis tick={{ fontSize: 12, fill: '#5A5A5A' }} tickFormatter={(v) => `${CURRENCY_SYMBOL}${v}`} />
                <Tooltip formatter={(v) => `${CURRENCY_SYMBOL}${v}`} contentStyle={{ borderRadius: 12, border: '1px solid #E5E5E0' }} />
                <Line type="monotone" dataKey="revenue" stroke="#E07A5F" strokeWidth={2} dot={{ fill: '#E07A5F', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-[#E5E5E0] bg-white" style={{ boxShadow: 'var(--shadow-soft)' }}>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4">Monthly Bookings</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#5A5A5A' }} />
                <YAxis tick={{ fontSize: 12, fill: '#5A5A5A' }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E5E5E0' }} />
                <Bar dataKey="bookings" fill="#2D2D2D" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Clients */}
      {data.top_clients?.length > 0 && (
        <Card className="rounded-2xl border-[#E5E5E0] bg-white" style={{ boxShadow: 'var(--shadow-soft)' }}>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4">Top Clients</h3>
            <div className="space-y-2">
              {data.top_clients.slice(0, 10).map((c, i) => (
                <div key={i} className="flex items-center justify-between bg-[#F9F9F7] rounded-xl p-3" data-testid={`top-client-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#2D2D2D] text-white text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                    <span className="text-sm font-medium text-[#2D2D2D]">{c.name}</span>
                  </div>
                  <span className="text-sm text-[#888]">{c.bookings} bookings</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
